import _ from 'underscore';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Toggle, CommandButton, Checkbox } from '@fluentui/react';
import { useIsBusinessHourSectionEnabled } from '@bingads-webui-campaign-react/smart-page-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getIsOpenAllDay } from '@bingads-webui-campaign-react/smart-component-util';

import { Mode } from './data-config';
import { TimeRangeEditor as TimeRangeEditorOld } from './time-range-editor';
import { TimeRangeEditor as TimeRangeEditorNew } from './time-range-editor-new';
import {
  getDayName,
  getNextHour,
  validateTimeSlots,
  reIndexTimeSlots,
} from './time-utils';

const getStyles = () => ({
  container: {
    margin: 0,
  },
  containerInline: {
    display: 'flex',
    alignItems: 'start',

    '& $toggle': {
      marginTop: 5,
      marginRight: 20,
      minWidth: 120,
      justifyContent: 'unset',

      '& .ms-Toggle-label': {
        marginLeft: 8,
      },
    },

    '& $timeActionContainer': {
      marginTop: 5,
      marginLeft: 12,
    },
  },
  toggle: {
    justifyContent: 'space-between',
  },
  error: {
    color: '#d13438',
  },
  addButton: {
    padding: 0,
  },
  timeActionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  timeRangeContainer: {
    '& .ms-Checkbox': {
      flexDirection: 'row-reverse',
    },
  },
});

const addButtonStyle = {
  label: {
    color: '#0078d4',
  },
  labelDisabled: {
    color: '#a19f9d',
  },
};

const toggleStyles = {
  container: {
    margin: '10px 0px',
    display: 'flex',
  },
};

const checkBoxStyle = {
  root: {
    marginRight: '32px',
  },
};

const defaultTimeSlot = {
  index: 0,
  openHour: 9,
  openMinute: 0,
  closeHour: 17,
  closeMinute: 0,
  isValid: true,
};

const getIsCheckAllDay = (value, isBusinessHourSectionEnabled) => {
  // isCheckAllDay is only available under the pilot "IsBusinessHourSectionEnabled"
  if (!isBusinessHourSectionEnabled) {
    return false;
  }

  return getIsOpenAllDay(value);
};

export const UnStyledSmartDayEditor = ({
  classes,
  i18n,
  isDisabled,
  value, // current day's data
  onChange,
  mode,
}) => {
  const isBusinessHourSectionEnabled = useIsBusinessHourSectionEnabled();

  const [isOpen, setIsOpen] = useState(!_.isEmpty(value.timeSlots));
  const [canAdd, setCanAdd] = useState(value.isValid);
  // This field can contain invalid timeslots while SiteConfig not.
  const [timeSlots, setTimeSlots] = useState(reIndexTimeSlots(value.timeSlots));
  const [showError, setShowError] = useState(false);
  const [isCheckAllDay, setIsCheckAllDay] = useState(getIsCheckAllDay(
    value,
    isBusinessHourSectionEnabled
  ));

  const TimeRangeEditor = isBusinessHourSectionEnabled ? TimeRangeEditorNew : TimeRangeEditorOld;

  // Update current day's data into SiteConfig
  const triggerChange = useCallback((updatedTimeSlots) => {
    const isValid = validateTimeSlots(updatedTimeSlots);
    // TODO: _.last can't get the correct last timeslot, need retrieve the biggest index item
    const last = _.last(updatedTimeSlots);
    if ((last && last.openHour >= 23) || !isValid) {
      setCanAdd(false);
    } else {
      setCanAdd(true);
    }

    setShowError(!isValid);
    // Don't update siteConfig if current timeslots are invalid
    if (!isValid) {
      return;
    }

    const newDayData = {
      day: value.day,
      timeSlots: updatedTimeSlots,
      isValid,
    };
    newDayData.isOpenAllDay = getIsCheckAllDay(newDayData, isBusinessHourSectionEnabled);

    setIsCheckAllDay(newDayData.isOpenAllDay);
    onChange(newDayData); // Modify the data in SiteConfig
  }, [value.day, isBusinessHourSectionEnabled, onChange]);

  // Control if a day is open.
  const onToggleChange = useCallback((e, checked) => {
    const newTimeSlots = checked ? [defaultTimeSlot] : [];

    setIsOpen(checked);
    setTimeSlots(newTimeSlots);
    triggerChange(newTimeSlots);
  }, [triggerChange]);

  // Update a specific timeslot
  const onTimeSlotChange = useCallback((updatedTimeSlot) => {
    const newTimeSlots = _.map(
      timeSlots,
      ts => (ts.index === updatedTimeSlot.index ? updatedTimeSlot : ts)
    );

    setTimeSlots(newTimeSlots);
    triggerChange(newTimeSlots);
  }, [timeSlots, triggerChange]);

  // Add a new timeslot
  const onAdd = useCallback(() => {
    const last = _.last(timeSlots);
    let newTimeSlots;

    if (last) {
      const newIndex = last ? last.index + 1 : 0;
      const nextHour = getNextHour(last.closeHour);
      const newTimeSlot = {
        index: newIndex,
        openHour: nextHour,
        openMinute: 0,
        closeHour: nextHour,
        closeMinute: 30,
        isValid: true,
      };
      newTimeSlots = _.union(timeSlots, [newTimeSlot]);
    } else {
      newTimeSlots = [defaultTimeSlot];
    }

    setTimeSlots(newTimeSlots);
    triggerChange(newTimeSlots);
  }, [timeSlots, triggerChange]);

  // Remove a specific timeslot
  const onRemove = useCallback((index) => {
    const newTimeSlots = _.reject(timeSlots, ts => ts.index === index);

    setTimeSlots(newTimeSlots);
    triggerChange(newTimeSlots);
  }, [timeSlots, triggerChange]);

  const onCheckAllDay = useCallback((event, checked) => {
    setIsCheckAllDay(checked);
    // check: Set time slots as 12:00 AM - 11:59 PM
    // unCheck: Set time slots as default
    const newTimeSlots = checked ? [{
      index: 0,
      openHour: 0,
      openMinute: 0,
      closeHour: 23,
      closeMinute: 59,
      isValid: true,
    }] : [defaultTimeSlot];

    setTimeSlots(newTimeSlots);
    triggerChange(newTimeSlots);
  }, [triggerChange]);

  const currentDayLabel = getDayName(value.day, i18n);

  // Render the day open toggle
  const dayToggle = useMemo(() => (
    <Toggle
      className={classes.toggle}
      label={currentDayLabel}
      inlineLabel
      defaultChecked={isOpen}
      disabled={isDisabled}
      onChange={onToggleChange}
      styles={toggleStyles}
      {
        ...(
          mode === Mode.VERTICAL
          ? {
            onText: i18n.getString(_TL_('Open')),
            offText: i18n.getString(_TL_('Closed')),
          }
          : {}
        )
      }
    />
  ), [classes.toggle, currentDayLabel, i18n, isDisabled, isOpen, mode, onToggleChange]);

  // Render the add hours button
  const addTimeRange = useMemo(() => !isCheckAllDay && (
    <CommandButton
      className={classNames(classes.addButton, 'add-button')}
      disabled={!canAdd}
      onClick={onAdd}
      iconProps={{ iconName: 'Add' }}
      text={i18n.getString(_TL_('Add more open hours'))}
      styles={addButtonStyle}
      ariaLabel={
        i18n.getString(_TL_('Add more open hours to {{day}}'), { day: currentDayLabel })
      }
    />
  ), [canAdd, classes.addButton, currentDayLabel, i18n, isCheckAllDay, onAdd]);

  // Render the open all day checkbox
  const openAllDayElement = useMemo(() => isBusinessHourSectionEnabled &&
    (
      <Checkbox
        label={i18n.getString(_TL_('Open all day'))}
        checked={isCheckAllDay}
        onChange={onCheckAllDay}
        styles={timeSlots.length > 1 ? checkBoxStyle : {}}
      />
    ), [
    i18n, isBusinessHourSectionEnabled, isCheckAllDay,
    onCheckAllDay, timeSlots.length,
  ]);

  // Render the time range editor list
  const timeRangeEditorList = useMemo(() => (
    <div className={classes.timeRangeList}>
      {
          timeSlots.map(timeSlot => (
            <TimeRangeEditor
              key={`${value.day}-${timeSlot.index}`}
              i18n={i18n}
              isDisabled={isDisabled || isCheckAllDay}
              isRemovable={timeSlots.length > 1}
              index={timeSlot.index}
              open={{ hour: timeSlot.openHour, minute: timeSlot.openMinute }}
              close={{ hour: timeSlot.closeHour, minute: timeSlot.closeMinute }}
              onChange={onTimeSlotChange}
              onRemove={onRemove}
              currentDay={currentDayLabel}
              isCheckAllDay={isCheckAllDay}
            />
          ))
      }
      {
        showError && (
          <div className={classNames(classes.error, 'time-ranges-error')}>
            {i18n.getString(_TL_('Invalid time ranges'))}
          </div>
        )
      }
    </div>
  ), [
    classes.error, classes.timeRangeList, currentDayLabel, i18n,
    isCheckAllDay, isDisabled, onRemove, onTimeSlotChange, showError,
    timeSlots, value.day,
  ]);

  if (mode === Mode.INLINE) {
    return (
      <div
        className={classNames(classes.containerInline, 'smart-day-editor')}
        data-name={value.day}
      >
        {dayToggle}
        <div className={classes.timeRangeContainer}>
          {timeRangeEditorList}
          {!isDisabled && isOpen && openAllDayElement}
        </div>
        {
          !isDisabled && isOpen && (
            <div className={classes.timeActionContainer}>
              {addTimeRange}
            </div>
          )
        }
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.container, 'smart-day-editor')}
      data-name={value.day}
    >
      {dayToggle}
      {timeRangeEditorList}
      {
        !isDisabled && isOpen && (
          <div className={classes.timeActionContainer}>
            {addTimeRange}
            {openAllDayElement}
          </div>
        )
      }
    </div>
  );
};

UnStyledSmartDayEditor.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
    addButton: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isDisabled: PropTypes.bool.isRequired,
  value: PropTypes.shape({
    day: PropTypes.string.isRequired,
    timeSlots: PropTypes.arrayOf(PropTypes.object),
    isValid: PropTypes.bool.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
};

export const SmartDayEditor = withDefaultStyles(
  UnStyledSmartDayEditor,
  getStyles
);
